import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

const SettingsModalTabs = [
  'account',
  'subscription',
  'help',
  'calendars',
  'integrations',
  'organizations',
  'createOrganization',
] as const;

export type SettingsModalTab = (typeof SettingsModalTabs)[number];

export const isSettingsModalTab = (
  value: string
): value is SettingsModalTab => {
  return SettingsModalTabs.includes(value as SettingsModalTab);
};

type State = {
  isOpen: boolean;
  tab: SettingsModalTab;
  // Which org the 'organizations' tab shows; meaningless for other tabs.
  // Deliberately decoupled from the global active org context — browsing an
  // org's settings must not switch the library/creation context.
  organizationId: string | null;
  open: (tab?: SettingsModalTab, organizationId?: string) => void;
  close: () => void;
  changeTab: (tab: SettingsModalTab, organizationId?: string) => void;
};

const DEFAULT_TAB: SettingsModalTab = 'account';

export const useSettingsModal = createWithEqualityFn<State>()(
  (set) => ({
    isOpen: false,
    tab: DEFAULT_TAB,
    organizationId: null,
    open: (tab: SettingsModalTab = DEFAULT_TAB, organizationId?: string) =>
      set({ tab, organizationId: organizationId ?? null, isOpen: true }),
    close: () => set({ isOpen: false }),
    changeTab: (tab: SettingsModalTab, organizationId?: string) =>
      set({ tab, organizationId: organizationId ?? null }),
  }),
  shallow
);

export const useOpenSettingsModal = () => {
  const open = useSettingsModal((state) => state.open);
  return { openSettingsModal: open };
};
